import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ASYNC_TASK_MAX_PRIORITY, IAsyncTask } from '../../types/asyncTask';
import styled from 'styled-components';
import { faBug, faHourglassStart, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ModuleContext from '../context/ModuleContext';
import { get, put } from '../../services/request';
import KeyContext from '../context/KeyContext';
import { createKeyPath } from '../context/helper/keyContext';

type IProps = {
  task: IAsyncTask<any>;
  keyValue?: string;
  children?: React.ReactNode;
};

const POLLING_INTERVAL = 5000;

const AsyncTaskWrapper: React.FC<IProps> = ({ task: initialTask, keyValue, children }) => {
  const module = useContext(ModuleContext);
  const parentKey = useContext(KeyContext);
  const aggregatedKeyName = keyValue ? createKeyPath(parentKey, keyValue) : parentKey;
  const [task, setTask] = useState<IAsyncTask<null>>(initialTask);
  const [polling, setPolling] = useState<boolean>(initialTask.status !== 2);
  const isFetchingRef = useRef(false);

  const handlePriority = useCallback(() => {
    put(`/async-tasks/${task.id}/increase-priority`, {});
    setTask((prevState) => ({
      ...prevState,
      priority: ASYNC_TASK_MAX_PRIORITY,
    }));
  }, []);

  useEffect(() => {
    setTask(initialTask);
    setPolling(initialTask.status !== 2);
  }, [initialTask]);

  useEffect(() => {
    if (!polling) return; // already finished – nothing to poll

    const interval = setInterval(async () => {
      if (!polling) return;
      if (isFetchingRef.current) return; // prevent overlapping requests
      isFetchingRef.current = true;
      try {
        const response = await get<IAsyncTask<null>>(`/async-tasks/${task.id}`);
        if (response.status === 2) {
          module.updateAsyncValues(aggregatedKeyName);
          setPolling(false);
        } else {
          if (task.status !== response.status || task.id !== response.id) {
            setTask(response);
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      } finally {
        isFetchingRef.current = false;
      }
    }, POLLING_INTERVAL);

    return () => {
      clearInterval(interval); // clean up on unmount
      isFetchingRef.current = false;
    };
  }, [task.id, module, polling, aggregatedKeyName]);

  if (task.status === 2) {
    return <>{children}</>;
  }

  return (
    <Wrapper>
      <ChildrenWrapper>{children}</ChildrenWrapper>

      <TaskLayer>
        {task.status === 0 && (
          <Icon icon={faHourglassStart} cur={true} onClick={handlePriority} priority={task.priority} />
        )}
        {task.status === 1 && <Icon icon={faSpinner} spin />}
        {task.status > 2 && <Icon icon={faBug} />}
      </TaskLayer>
    </Wrapper>
  );
};

export default AsyncTaskWrapper;

const Wrapper = styled.div`
  position: relative;
`;

const TaskLayer = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(238, 142, 17, 0.23);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Icon = styled(FontAwesomeIcon)<{ priority?: number; cur?: boolean }>`
  color: ${(props) => (props.priority === ASYNC_TASK_MAX_PRIORITY ? '#ff0000' : '#ee8e11')};
  cursor: ${(props) => (props.cur && props.priority !== ASYNC_TASK_MAX_PRIORITY ? 'pointer' : 'default')};
`;

const ChildrenWrapper = styled.div`
  opacity: 0;
`;
